import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";

import {
  defaultRecommendationsSettings, horizontalAlignments, RecommendationCompactHistory,
  recommendationFonts, recommendationFontSizes, RecommendationMeta,
  RecommendationsSettings,
  RecommendationStyle, RecommendationStyleType,
  Settings
} from '../../../../../core/models';
import {FormGroupRecord} from '../../../../../core/types/form.type';
import {Subject, takeUntil} from 'rxjs';
import {ColorPickerDirective} from 'ngx-color-picker';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {uniq} from '../../../../../core/helpers/uniq';

type RecommendationsSettingsForm = RecommendationsSettings & { styleConfig?: RecommendationStyle };
type IndividualPrintLayout = {
  meta: ({
    control: 'amount' | 'author',
    label: string,
  } | {
    key: RecommendationMeta,
    label: string,
    checked?: boolean,
  })[][]
}

@Component({
    selector: 'app-recommendations-settings-dialog',
    templateUrl: './recommendations-settings-dialog.component.html',
    styleUrls: ['./recommendations-settings-dialog.component.scss'],
    standalone: false
})
export class RecommendationsSettingsDialogComponent implements OnInit, OnDestroy {

  settings: RecommendationsSettingsForm;

  form: FormGroupRecord<RecommendationsSettingsForm, { style: FormControl<RecommendationStyleType|null>, individual_print: { meta: FormControl<RecommendationMeta[]> } }> = new FormGroup({
    warning_multiple_applications: new FormControl(false),
    hide_priority: new FormControl(true),
    show_private_name: new FormControl(false),
    show_private_address: new FormControl(false),
    style: new FormControl<RecommendationStyleType|null>('ap'),
    compact_history: new FormControl<RecommendationCompactHistory|null>('digest'),
    attachment_scale: new FormControl(0.85,
      [Validators.required, Validators.min(0.1), Validators.max(1)]),
    priorities: new FormArray<FormControl>([], Validators.required),
    exceeding_grant_warning: new FormControl(false),
    styleConfig: new FormGroup({
      font: new FormControl(null),
      footer: new FormGroup({
        text: new FormControl(null),
        options: new FormGroup({
          align: new FormControl(null),
          size: new FormControl(null),
          leading: new FormControl(null),
          color: new FormControl(null, this.colorValidator()),
          character_spacing: new FormControl(null)
        })
      }),
      logo: new FormGroup({
        width: new FormControl(null, Validators.max(400)),
        position: new FormControl(null)
      }),
      font_sizes: new FormGroup({
        metadata: new FormControl(null),
        content: new FormControl(null),
        toc: new FormControl(null),
        recommended_amount: new FormControl(null),
        preamble_box: new FormControl(null),
        footer_page_number: new FormControl(null),
        preamble_title: new FormControl(null),
        category_name: new FormControl(null),
        heading_project: new FormControl(null)
      })
    }),
    individual_print: new FormGroup({
      author: new FormControl(false),
      amount: new FormControl(false),
      description: new FormControl(false),
      spiderweb: new FormControl(false),
      history: new FormControl(null),
      meta: new FormControl([])
    })
  });
  subPriorityAmount: number = 7;
  individualPrintLayout: IndividualPrintLayout = {
    meta: [
      [
        { key: 'journal_no', label: 'Journalnummer' },
        { key: 'category', label: 'Kategori (alfabetisk)' },
        { key: 'subcategory', label: 'Underkategori' },
        { key: 'applicant', label: 'Ansøger' }
      ],
      [
        { key: 'period', label: 'Projektperiode' },
        { key: 'applied', label: 'Ansøgt beløb' },
        { key: 'own_funding', label: 'Egen finansiering' },
        { key: 'needed_amount', label: 'Støttebehov' },
        { key: 'granted_others', label: 'Ekstern finansiering' },
        { key: 'total', label: 'Samlet budget' }
      ],
      [
        { control: 'amount', label: 'Indstillet beløb' },
        { key: 'other_funds', label: 'Andre tilskud søgt?' },
        { key: 'case_workers', label: 'Sagsbehandler' },
        { control: 'author', label: 'Indstiller' }
      ],
      [
        { key: 'committee_amount', label: 'Bevil. indskrivningsfelt' },
        { key: 'meeting', label: 'Mødedato' }
      ]
    ]
  };
  showHistory: FormControl<boolean> = new FormControl(false);

  horizontalAlignments = horizontalAlignments
  fontSizes = recommendationFontSizes;
  fonts = recommendationFonts;
  hasFooter: FormControl<boolean> = new FormControl(false);
  destroy$: Subject<boolean> = new Subject();

  @ViewChild(ColorPickerDirective)
  picker!: ColorPickerDirective;

  constructor(public dialogRef: MatDialogRef<RecommendationsSettingsDialogComponent>,
              @Inject(MAT_DIALOG_DATA) private data: Settings) { }

  ngOnInit(): void {
    this.settings = {
      warning_multiple_applications: false,
      hide_priority: true,
      show_private_name: false,
      show_private_address: false,
      style: 'standard',
      compact_history: 'digest',
      attachment_scale: 0.85,
      exceeding_grant_warning: false,
      priorities: ['1.0', '2.0'],
      individual_print: {
        author: false,
        amount: false,
        description: false,
        spiderweb: false,
        history: null,
        meta: ['journal_no', 'category', 'subcategory', 'applicant', 'period', 'total', 'applied']
      },
      ...structuredClone(this.data['recommendations'] || {})
    };
    this.initFooterToggle();
    this.initPriorities();
    this.initStyleConfig();
    this.form.patchValue(this.settings as any);
    this.form.updateValueAndValidity();
    this.initShowHistory();
    this.initCheckedMeta();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  private initShowHistory(): void {
    const controls = this.form.controls.individual_print.controls;
    this.showHistory.setValue(!!controls.history.value);
    this.showHistory.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      if (value) return;

      controls.history.setValue(null);
    });
  }

  private initCheckedMeta(): void {
    const list = this.form.controls.individual_print.controls.meta.value;
    this.individualPrintLayout.meta.forEach(group => {
      group.forEach(entry => {
        if ('control' in entry) return;

        entry.checked = list.includes(entry.key);
      });
    });
  }

  private colorValidator(): ValidatorFn {
    return (control: FormControl): ValidationErrors => {
      const value = control.value;
      return value && !/^[0-9a-fA-F]{6}$/.test(value) ? { 'color': { value: control.value } } : null;
    };
  }

  private initFooterToggle(): void {
    this.hasFooter.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value) {
          this.form.controls.styleConfig.controls.footer.enable();
        } else {
          this.form.controls.styleConfig.controls.footer.disable();
        }
      })
  }

  private initStyleConfig(): void {
    if (typeof this.settings.style == 'string') {
      this.settings.styleConfig = {};
    } else {
      this.settings.styleConfig = this.settings.style;
      this.settings.style = 'standard';
      this.hasFooter.setValue(!!this.settings?.styleConfig?.footer?.text);
    }
  }

  doSave(): void {
    this.dialogRef.close(this.form);
  }

  initPriorities(): void {
    if (!this.settings.priorities) {
      this.settings.priorities = defaultRecommendationsSettings.priorities;
    }
    this.settings?.priorities.forEach(value => this.doAppendPriority(value))
    this.subPriorityAmount = this.settings.priorities.length / 2;
  }

  doAppendPriority(value: string): void {
    this.priorities.push(new FormControl(value));
  }

  setPriorityValues(): void {
    this.priorities.clear();
    const list = Array.from(Array(this.subPriorityAmount).keys());
    [1, 2].forEach(p =>  {
      list.forEach(n => this.priorities.push(new FormControl(`${p}.${n}`)))
    });
  }

  get formatAttachmentScale(): string {
    const value = this.attachmentScale.value;
    return (value * 100).toFixed(0) + '%';
  }

  get attachmentScale(): FormControl {
    return this.form.get('attachment_scale') as FormControl;
  }

  get priorities(): FormArray {
    return this.form.get('priorities') as FormArray;
  }

  get hide_priority(): FormControl {
    return this.form.get('hide_priority') as FormControl;
  }

  setFooterColor(color: string): void {
    this.form.get(['styleConfig', 'footer', 'options', 'color']).setValue(color.replace('#', ''));
  }

  openPicker() {
    this.picker.openDialog();
  }

  toggleMeta($event: MatCheckboxChange, meta: RecommendationMeta): void {
    const control = this.form.controls.individual_print.controls.meta;
    let list = [...control.value];
    if ($event.checked) {
      list.push(meta);
    } else {
      list = list.filter(m => m !== meta);
    }
    control.setValue(uniq(list));
  }
}
